import {createStore, applyMiddleware, compose} from 'redux';
import thunk from 'redux-thunk';
import {Map, fromJS, is} from 'immutable';
import {BATCH_ACTIONS, STATE} from './../constants/index';
import {CONFIG} from '../appConfig';
import requestReducer from './../reducers/request';
import cleanReducer from './../reducers/clean';
import filtersReducer from './../reducers/filters';
import mapReducer from './../reducers/map';
import initialState, {car_and_driver, cda} from './../initialState';
import {types} from './js/types';

/**
 * @private
 * @desc The reducers we use for redux action handling
 * @type {Array}
 */
let reducers = [];

/**
 * @private
 * @desc If reducer is a function, adds it to the master reducers array.
 * @param {Function} reducer
 */
function addToReducers(reducer) {
  if (typeof reducer === 'function') {
    reducers.push(reducer);
  }
}

/**
 * @private
 * @desc Enable batching for dispatching multiple actions
 * @param {Function} reducer
 * @returns {Object}
 */
function enableBatching(reducer) {
  return function batchingReducer(state, action) {
    switch (action.type) {
      case BATCH_ACTIONS:
        return action.actions.reduce(batchingReducer, state);
      default:
        return reducer(state, action);
    }
  };
}

/**
 * @private
 * @desc Combines smaller reducer functions into a larger object of reducers to manage state.
 * @param {Object} state An immutable state object to check against
 * @param {Object} action Data to update the redux store state
 * @returns {Object} A (usually changed) immutable state object
 */
function reducer(state = Map(), action) {
  for (let i=0; i<reducers.length; i++) {
    let nextState = reducers[i](state, action);
    if (!is(state, nextState)) {
      return nextState;
    }
  }
  return state;
}

/**
 * @private
 * @desc Allows us to set, and reset, reducer functions used when handling dispatched redux actions
 * @param {Array} initialReducers
 */
function setReducers(initialReducers) {
  if (!Array.isArray(initialReducers)) initialReducers = [];
  reducers = initialReducers.concat([
    cleanReducer,
    filtersReducer,
    mapReducer,
    requestReducer
  ]);
}

/**
 * @name batchActions
 * @desc Batch Action Creator
 * @param {...Function} actions
 * @returns {Object} {type: string, actions: []}
 * @memberof utils.redux
 * @example
 * store.dispatch(
 *  redux.batchActions(
 *   doSomething(),
 *   doSomethingElse()
 *  )
 * );
 */
export function batchActions(...actions) {
  return {type: BATCH_ACTIONS, actions};
}

/**
 * @name combineReducers
 * @desc Combines smaller reducers into a larger array of reducers to manage state.
 * @param {Function|Array} newReducers
 * @memberof utils.redux
 * @example
 * const reducer1 = () => {
 *  switch (action.type) {
 *   case 'TEST':
 *    state = doSomething(state);
 *    return state;
 *   default:
 *    return state;
 *  }
 * }
 * redux.combineReducers(reducer1); // ...or...
 * redux.combineReducers([reducer1]);
 */
export function combineReducers(newReducers) {
  if (Array.isArray(newReducers)) {
    newReducers.forEach(reducer => addToReducers(reducer));
  } else {
    addToReducers(newReducers);
  }
}


/**
 * @private
 * @desc compose middleware, allow for project specific middleware in addition to GMI middleware
 * @param {Array|Function} middleware
 */
function composeMiddleware(middleware = []) {
  const composeEnhancers = typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

  if (!Array.isArray(middleware)) middleware = [middleware];
  return composeEnhancers(applyMiddleware(...[thunk].concat(middleware)));
}

/**
 * This is public within GMI, but not available in the public API
 * @private
 * @desc Create a redux store instance, possibly with preloaded data.
 * @param {Object} initialAppState An object of application specific bootstrapped data
 * @param {Array} initialReducers An array of reducers to add when first creating store
 * @param {Array|Function} middleware
 * @param {Object} pathConfig An object with custom app and session paths
 * @returns {Object} A redux store instance
 * @memberof utils.redux
 */
export function makeStore(initialAppState = {}, initialReducers = [], middleware, pathConfig = {}) {
  const {app, session} = pathConfig;
  if(types(CONFIG.CDA_ORIGIN_URL).isString && CONFIG.CDA_ORIGIN_URL !== '') {
    initialState.gmi.cda = cda;
    initialState.gmi.ui.car_and_driver = car_and_driver;
  }
  let state = fromJS(initialState);
  if(app) {
    //Delete default app object defined by initialState if custom path provided in pathConfig and update STATE cursor with new path
    state = state.deleteIn(STATE.APP);
    STATE.APP = app;
  }
  if(session) {
    //Delete default session object defined by initialState if custom path provided. Set default session object to new path and update STATE cursor
    const initialSessionObj = state.getIn(STATE.SESSION);
    state = state.deleteIn(STATE.SESSION);
    state = state.setIn(session, initialSessionObj);
    STATE.SESSION = session;
  }
  state = state.mergeIn(STATE.APP, fromJS(initialAppState));

  setReducers(initialReducers);
  return createStore(enableBatching(reducer), state, composeMiddleware(middleware));
}

/**
 * @name observeStore
 * @desc Wrap redux store.subscribe to return state when nested state prop has changed.
 * @param {Object} store
 * @param {Function} select
 * @param {Function} onChange
 * @returns {Function} unsubscribe method
 * @memberof utils.redux
 * @example
 * const unsubscribe = observeStore(store, state => state.test, state => {
 *  doSomething(state);
 *  unsubscribe();
 * });
 */
export function observeStore(store, select, onChange) {
  let currentValue;
  function handleChange() {
    let previousValue = currentValue,
      currState = store.getState();
    currentValue = select(currState);
    if ((typeof previousValue !== 'undefined') && previousValue !== currentValue) onChange(currState);
  }
  let unsubscribe = store.subscribe(handleChange);
  handleChange();
  return unsubscribe;
}

/**
 * @name casteImmutable
 * @desc Convert to Immutable if Object or Array, return strings unchanged
 * @param {Object|Array|String} value
 * @returns {Object|Array|String}
 * @memberof utils.redux
 * @example
 * const value = casteImmutable(action.value);
 */
export function casteImmutable(value) {
  const type = types(value);
  return (type.isArray || type.isObject) ? fromJS(value) : value;
}

/**
 * @namespace "utils.redux"
 */
export default {
  batchActions,
  combineReducers,
  observeStore,
  casteImmutable
};
